import type { ExpressionSpecification, Map as MapboxMap } from "mapbox-gl";
import type { RoadsResponse, GeoJsonFeature, RoadFeatureProperties } from "../../api/map/types";
import { applyIncrementalUpdate, setCachedFeatures } from "./utils";

const SOURCE_ID = "roads";

/**
 * Roads only exist to be clicked when you are close enough to see a single street; further
 * out they are the heaviest layer on the map for no benefit, so both the layer and its
 * fetch stop below this zoom (see the roadsQuery `enabled` guard in MapView).
 */
export const ROADS_MIN_ZOOM = 15;

// Shared base styling (used both when creating the layer and when restoring after highlight)
const ROAD_LINE_COLOR = "#64748b"; // Darker gray for better visibility
const ROAD_LINE_OPACITY = 0.45;
const ROAD_LINE_WIDTH: ExpressionSpecification = [
    "interpolate",
    ["linear"],
    ["zoom"],
    10,
    2,
    12,
    3,
    15,
    4,
    18,
    5.5
];

/**
 * Ensures the roads layer exists on the map (idempotent)
 */
export const ensureRoadsLayer = (map: MapboxMap): void => {
    // Check if map style is loaded before adding sources
    if (!map.isStyleLoaded()) {
        map.once("style.load", () => {
            ensureRoadsLayer(map);
        });
        return;
    }

    if (!map.getSource(SOURCE_ID)) {
        map.addSource(SOURCE_ID, {
            type: "geojson",
            data: { type: "FeatureCollection", features: [] }
        });
    }

    if (!map.getLayer(`${SOURCE_ID}-line`)) {
        map.addLayer({
            id: `${SOURCE_ID}-line`,
            type: "line",
            source: SOURCE_ID,
            minzoom: ROADS_MIN_ZOOM,
            paint: {
                "line-color": ROAD_LINE_COLOR,
                "line-width": ROAD_LINE_WIDTH,
                "line-opacity": ROAD_LINE_OPACITY
            }
        });
    }
};

/**
 * Updates the roads layer with data from the API
 * Uses incremental updates when possible to minimize re-rendering
 */
export const updateRoadsLayer = (
    map: MapboxMap,
    data: RoadsResponse | null,
    useIncremental: boolean = true
): void => {
    const source = map.getSource(SOURCE_ID);
    if (!source || source.type !== "geojson") {
        console.warn("[RoadsLayer] Source not found or wrong type");
        return;
    }

    const geojsonSource = source as mapboxgl.GeoJSONSource;

    if (!data || data.features.length === 0) {
        geojsonSource.setData({ type: "FeatureCollection", features: [] });
        return;
    }

    // Convert API response to Mapbox GeoJSON format
    const newFeatures: GeoJsonFeature<RoadFeatureProperties>[] = data.features.map((feature) => ({
        type: "Feature" as const,
        id: feature.id ?? feature.properties.roadSegmentId,
        geometry: feature.geometry,
        properties: {
            ...feature.properties,
            roadSegmentId: feature.properties.roadSegmentId
        }
    }));

    // Use incremental update if enabled
    // For roads, use keepExistingFeatures=true to prevent roads from disappearing when zooming out
    if (useIncremental) {
        try {
            applyIncrementalUpdate(
                map,
                geojsonSource,
                SOURCE_ID,
                newFeatures,
                (feature) => feature.id ?? feature.properties.roadSegmentId,
                true // keepExistingFeatures: keep all roads, only add new ones
            );
            return;
        } catch (error) {
            // Fallback to full update on error
            console.warn("Incremental update failed, falling back to full update:", error);
        }
    }

    // Full update (incremental disabled or error)
    const geojson = {
        type: "FeatureCollection" as const,
        features: newFeatures
    };

    geojsonSource.setData(geojson as any);
    setCachedFeatures(map, SOURCE_ID, newFeatures);
};

/**
 * Highlights a specific road segment by setting a filter on the roads layer
 */
export const highlightRoadSegment = (map: MapboxMap, roadSegmentId: number | null): void => {
    const layerId = `${SOURCE_ID}-line`;
    const layer = map.getLayer(layerId);

    if (!layer) {
        return;
    }

    if (roadSegmentId === null) {
        // Remove filter to show all roads
        map.setFilter(layerId, null);
    } else {
        // Filter to show only the selected road segment with highlight
        map.setFilter(layerId, ["==", ["get", "roadSegmentId"], roadSegmentId]);

        // Update paint properties for highlight
        map.setPaintProperty(layerId, "line-color", "#8b5cf6"); // Purple for highlight
        map.setPaintProperty(layerId, "line-width", [
            "interpolate",
            ["linear"],
            ["zoom"],
            10,
            5,
            12,
            6,
            15,
            7,
            18,
            9
        ]);
        map.setPaintProperty(layerId, "line-opacity", 1.0);
    }
};

/**
 * Removes highlight from road segment and restores normal styling
 */
export const removeRoadSegmentHighlight = (map: MapboxMap): void => {
    const layerId = `${SOURCE_ID}-line`;
    const layer = map.getLayer(layerId);

    if (!layer) {
        return;
    }

    // Remove filter to show all roads
    map.setFilter(layerId, null);

    // Restore normal styling
    map.setPaintProperty(layerId, "line-color", ROAD_LINE_COLOR);
    map.setPaintProperty(layerId, "line-width", ROAD_LINE_WIDTH);
    map.setPaintProperty(layerId, "line-opacity", ROAD_LINE_OPACITY);
};